import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { UserAPI } from '../../../api/UserAPI.js';
import { CLUB_PLAYER_SEARCH_DEBOUNCE_MS, canSearchClubPlayers } from '../../../util/reservationPlayerSearch';
import { ALL_MEMBERS_ID, searchSchedulerWho } from './schedulerForm.js';

const SchedulerWhoSearch = ({
    clubId,
    catalog = [],
    selected = [],
    canAttachAll = false,
    onPick,
}) => {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [box, setBox] = useState(null);
    const [searching, setSearching] = useState(false);
    const inputRef = useRef(null);

    const updateBox = () => {
        const el = inputRef.current;
        if (!el) {
            return;
        }
        const rect = el.getBoundingClientRect();
        setBox({
            top: rect.bottom + 4,
            left: rect.left,
            width: Math.max(rect.width, 240),
        });
    };

    useEffect(() => {
        let cancelled = false;
        const handle = window.setTimeout(async () => {
            if (!canSearchClubPlayers(query)) {
                if (!cancelled) {
                    setSuggestions([]);
                    setSearching(false);
                }
                return;
            }
            setSearching(true);
            updateBox();
            try {
                const next = await searchSchedulerWho({
                    query,
                    clubId,
                    catalog,
                    selected,
                    canAttachAll,
                    searchClubUsers: async (id, name) => {
                        const response = await UserAPI.getAllClubUserByName(id, name);
                        return response?.Data ?? response;
                    },
                });
                if (!cancelled) {
                    const selectedIds = new Set(selected.map((item) => String(item.id)));
                    setSuggestions(next.filter((item) => !selectedIds.has(String(item.id)) || item.id === ALL_MEMBERS_ID));
                    updateBox();
                }
            } catch {
                if (!cancelled) {
                    setSuggestions([]);
                }
            } finally {
                if (!cancelled) {
                    setSearching(false);
                }
            }
        }, CLUB_PLAYER_SEARCH_DEBOUNCE_MS);
        return () => {
            cancelled = true;
            window.clearTimeout(handle);
        };
    }, [query, clubId, catalog, selected, canAttachAll]);

    const pick = (item) => {
        onPick?.(item);
        setQuery('');
        setSuggestions([]);
    };

    const showList = canSearchClubPlayers(query) && (suggestions.length > 0 || searching);

    return (
        <div className="sm-place-field sm-scheduler-who-search">
            <input
                ref={inputRef}
                className="e-input sm-place-input"
                value={query}
                autoComplete="off"
                placeholder="Type 3 or more characters to search"
                onFocus={updateBox}
                onChange={(event) => setQuery(event.target.value)}
            />
            {showList && box && createPortal(
                <ul
                    className="sm-place-suggest"
                    data-testid="scheduler-who-suggestions"
                    style={{ top: box.top, left: box.left, width: box.width }}
                >
                    {searching && suggestions.length === 0 &&
                        <li className="sm-scheduler-who-empty">Searching…</li>}
                    {!searching && suggestions.length === 0 &&
                        <li className="sm-scheduler-who-empty">No players or teams found</li>}
                    {suggestions.map((item) =>
                        <li key={item.id}>
                            <button
                                type="button"
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    pick(item);
                                }}
                            >
                                <span>{item.name}</span>
                                {item.email ? <small>{item.email}</small> : null}
                            </button>
                        </li>)}
                </ul>,
                document.body
            )}
        </div>
    );
};

export default SchedulerWhoSearch;
